import { Badge, Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { WhatsApp, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { notificacionesWPAPI, turnosAPI, medicosAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const TIPOS_DESTINATARIO = [
  { id: 1, nombre: 'Administrador' },
  { id: 2, nombre: 'Técnico' },
  { id: 3, nombre: 'Administrativo' },
  { id: 4, nombre: 'Médico' },
  { id: 6, nombre: 'Supervisor' }
];

const GrillaNotificacionesWP = () => {
  const [reglas, setReglas] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(reglas);

  const [formData, setFormData] = useState({
    idEstadoTurno: '',
    destinatarios: '',
    nombrePlantilla: 'notificacion_estado_turno',
    tipoDestinatario: 3,
    idMedico: '',
    subEstado: '',
    activo: true
  });

  const SUB_ESTADOS = [
    { value: '', label: 'Todos (sin filtrar)' },
    { value: 'Reporte CPAP y Estudio', label: 'Reporte CPAP y Estudio' },
    { value: 'Reporte CPAP', label: 'Reporte CPAP' },
    { value: 'Estudio', label: 'Estudio' }
  ];

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [reglasRes, estadosRes, tecnicosRes] = await Promise.all([
        notificacionesWPAPI.listar(),
        turnosAPI.getEstadosSupervisor(),
        medicosAPI.getTecnicos()
      ]);
      setReglas(reglasRes.data || []);
      setEstados(estadosRes.data || []);
      setTecnicos(tecnicosRes.data || []);
    } catch {
      setToast({ message: 'Error al cargar las reglas.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = sortedData.filter(r =>
    !buscar ||
    textMatch(r.estadoTurno, buscar) ||
    textMatch(r.destinatarios, buscar) ||
    textMatch(r.medico, buscar)
  );

  const getNombreTipo = (id) => TIPOS_DESTINATARIO.find(t => t.id === id)?.nombre || '-';
  const esTecnico = formData.tipoDestinatario === 2;

  const handleNueva = () => {
    setEditando(null);
    setFormData({ idEstadoTurno: '', destinatarios: '', nombrePlantilla: 'notificacion_estado_turno', tipoDestinatario: 3, idMedico: '', subEstado: '', activo: true });
    setMostrarForm(true);
  };

  const handleEditar = (r) => {
    setEditando(r);
    setFormData({
      idEstadoTurno: r.idEstadoTurno,
      destinatarios: r.destinatarios,
      nombrePlantilla: r.nombrePlantilla,
      tipoDestinatario: r.tipoDestinatario,
      idMedico: r.idMedico || '',
      subEstado: r.subEstado || '',
      activo: r.activo
    });
    setMostrarForm(true);
  };

  const handleCancelar = () => { setMostrarForm(false); setEditando(null); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        idEstadoTurno: parseInt(formData.idEstadoTurno),
        destinatarios: formData.destinatarios.trim(),
        nombrePlantilla: formData.nombrePlantilla.trim(),
        tipoDestinatario: formData.tipoDestinatario,
        idMedico: esTecnico && formData.idMedico ? parseInt(formData.idMedico) : null,
        subEstado: String(formData.idEstadoTurno) === '19' ? formData.subEstado || null : null,
        activo: formData.activo
      };
      if (editando) {
        await notificacionesWPAPI.actualizar(editando.idRegla, payload);
        setToast({ message: 'Regla actualizada con éxito.', type: 'success' });
      } else {
        await notificacionesWPAPI.crear(payload);
        setToast({ message: 'Regla creada con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch {
      setToast({ message: 'Error al guardar la regla.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await notificacionesWPAPI.eliminar(confirmEliminar);
      setToast({ message: 'Regla eliminada con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar la regla.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <WhatsApp sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#25D366' }} />
            Notificaciones WhatsApp
          </h2>
          <span className="page-subtitle">Configurar reglas de envío automático por cambio de estado</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Regla' : 'Nueva Regla'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.5rem' }}>
              <Form.Group className="mb-3">
                <Form.Label>* Estado que dispara la notificación</Form.Label>
                <Form.Select
                  value={formData.idEstadoTurno}
                  onChange={(e) => setFormData(prev => ({ ...prev, idEstadoTurno: e.target.value, subEstado: '' }))}
                  required
                >
                  <option value="">Seleccione...</option>
                  {estados.map(e => (
                    <option key={e.idEstadoTurno} value={e.idEstadoTurno}>{e.estadoTurno}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              {String(formData.idEstadoTurno) === '19' && (
                <Form.Group className="mb-3">
                  <Form.Label>Sub Estado</Form.Label>
                  <Form.Select
                    value={formData.subEstado}
                    onChange={(e) => setFormData(prev => ({ ...prev, subEstado: e.target.value }))}
                  >
                    {SUB_ESTADOS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Vacío = aplica a todos los sub estados. Seleccionar uno = solo aplica a ese sub estado.
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>* Tipo de destinatario</Form.Label>
                <Form.Select
                  value={formData.tipoDestinatario}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipoDestinatario: parseInt(e.target.value), idMedico: '' }))}
                  required
                >
                  {TIPOS_DESTINATARIO.map(t => (
                    <option key={t.id} value={t.id}>{t.nombre}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  {esTecnico
                    ? 'Solo recibirá notificaciones de turnos donde esté asignado como técnico.'
                    : 'Recibirá notificaciones de todos los turnos que cambien a este estado.'}
                </Form.Text>
              </Form.Group>
              {esTecnico && (
                <Form.Group className="mb-3">
                  <Form.Label>* Técnico asignado</Form.Label>
                  <Form.Select
                    value={formData.idMedico}
                    onChange={(e) => setFormData(prev => ({ ...prev, idMedico: e.target.value }))}
                    required
                  >
                    <option value="">Seleccione...</option>
                    {tecnicos.map(t => (
                      <option key={t.idMedico} value={t.idMedico}>{t.medico1}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>* Destinatarios (números separados por ;)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="5491155291681;5491144556677"
                  value={formData.destinatarios}
                  onChange={(e) => setFormData(prev => ({ ...prev, destinatarios: e.target.value }))}
                  required
                />
                <Form.Text className="text-muted">
                  Formato: código de país + número, sin espacios ni guiones. Separar múltiples con ;
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>* Nombre de Plantilla (Meta)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="notificacion_estado_turno"
                  value={formData.nombrePlantilla}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombrePlantilla: e.target.value }))}
                  required
                />
                <Form.Text className="text-muted">
                  Nombre exacto del template aprobado en Meta Business.
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Activo"
                  checked={formData.activo}
                  onChange={(e) => setFormData(prev => ({ ...prev, activo: e.target.checked }))}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !formData.idEstadoTurno || !formData.destinatarios.trim() || !formData.nombrePlantilla.trim() || (esTecnico && !formData.idMedico)}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Buscar</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Estado, destinatario o técnico..."
                  value={buscar}
                  onChange={(e) => setBuscar(e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => {}} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => setBuscar('')}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Reglas configuradas</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{filtradas.length} reglas</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNueva} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nueva Regla
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idRegla')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idRegla') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('estadoTurno')} style={{ cursor: 'pointer' }}>
                    Estado {getSortIcon('estadoTurno') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Tipo</th>
                  <th>Sub Estado</th>
                  <th>Técnico</th>
                  <th>Destinatarios</th>
                  <th>Plantilla</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.length === 0 ? (
                  <tr><td colSpan="9" className="empty-state">{loading ? 'Cargando...' : 'No hay reglas configuradas'}</td></tr>
                ) : (
                  filtradas.map((r) => (
                    <tr key={r.idRegla}>
                      <td>{r.idRegla}</td>
                      <td>{r.estadoTurno}</td>
                      <td><Badge bg={r.tipoDestinatario === 2 ? 'info' : 'primary'}>{getNombreTipo(r.tipoDestinatario)}</Badge></td>
                      <td>{r.subEstado || 'Todos'}</td>
                      <td>{r.medico || '-'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.destinatarios}>
                        {r.destinatarios}
                      </td>
                      <td>{r.nombrePlantilla}</td>
                      <td>
                        <Badge bg={r.activo ? 'success' : 'secondary'}>{r.activo ? 'Sí' : 'No'}</Badge>
                      </td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(r)} onDelete={() => setConfirmEliminar(r.idRegla)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar regla"
        message="¿Estás seguro que querés eliminar esta regla de notificación?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaNotificacionesWP;
